import { jsPDF } from 'jspdf';
import { ref } from 'vue';
import axios from 'axios';

// IMPORTACIONES: ASUME que estas funciones están disponibles
import { formatCurrency, formatDate, getImageBase64 } from './useUtils';
import { useQrGenerator } from './useQrGenerator';
import { useNumberToText } from './useNumbersToText';
import { useLocationMapper } from './useLocationMapper';
/**
 * Genera el PDF en formato Carta para un Comprobante de Crédito Fiscal o Factura, según normas de El Salvador.
 * @param {Object} factura - Objeto completo de la factura (header y lines).
 * @param {Object} companyConfig - Objeto de configuración de la empresa.
 * @param {boolean} isReimpresion - Si es true, añade la marca de REIMPRESION.
 * @returns {Promise<void>}
 */
export function useDTEFiscal(factura, companyConfig, isReimpresion = false) {
    return new Promise((resolve) => {
        const { header, lines } = factura;
        const {
            client_name, client_nit, client_address,client_phone,
            subtotal, total_taxes, total, total_discount, date_issued, full_number, invoice_type,
            sub_total_brut, retention_amount, dte_url_code, dte_trace_code, dte_stamp_code, client_nrc, client_document_type, client_department_code,
            client_municipality_code, client_activity_code, client_activity_desc, client_email,  dte_type, payment_condition,
            payment_type, term_type, term, due_date
        } = header;
        const { numberToText } = useNumberToText();
        const { getLocationDescription,fetchLocations } = useLocationMapper();
        // --- Configuración de la Empresa ---
        const config = companyConfig;
        const logoUrl = config?.logo_url || '';
        const currencySymbol = config?.currency_symbol || '$';
        const footerNotes = config?.footer_notes || "Gracias por su compra.";
        const documentTypesWithoutIVA = ['01', '14']
        // Consumir el Composable de QR
        const dteUrlRef = ref(dte_url_code);
        const { qrDataUrl, generateQr } = useQrGenerator(dteUrlRef);

        if (lines.length === 0) {
            alert('La factura no tiene líneas de producto para imprimir.');
            return resolve();
        }
        const totalEnLetras = numberToText(total);


        console.log(`Monto a reportar: ${totalEnLetras}`);

        const documentTypeMap = {
            '13': 'DUI (13)',
            '36': 'NIT (36)',
            // Puedes añadir otros códigos aquí (ej: Pasaporte, Carnet de Residencia)
            // '99': 'PASAPORTE (99)',
        };
        const paymentOptions= {
            '01': '01 - Billetes y monedas' ,
            '02': '02 - Tarjeta Débito' ,
            '03': '03 - Tarjeta Crédito' ,
            '04': '04 - Cheque' ,
            '05': '05 - Transferencia-Depósito Bancario' ,
            '08': '08 - Dinero electrónico' ,
            '09': '09 - Monedero electrónico' ,
            '11': '11 - Bitcoin' ,
            '12': '12 - Otras Criptomonedas' ,
            '13': '13 - Cuentas por pagar del receptor' ,
            '14': '14 - Giro bancario' ,
            '99': '99 - Otros (se debe indicar el medio de pago)' ,
        };
        const term_types={
            '01':'Días',
            '02':'Meses',
            '03': 'Años'
        };


        // Nueva función helper para buscar el texto
        const getDocumentTypeText = (code) => {
            // Si el código no existe en el mapa, devolvemos el código original
            return documentTypeMap[code] || code;
        };

        let paymentOption = '';
        let termOption = '';
        if(dte_type!='05'){
            if (payment_condition === 1) { // 1 = Contado
                // Obtener la descripción del medio de pago (ej: '01 - Billetes y monedas')
                paymentOption = paymentOptions[payment_type] || payment_type;
            } else { // 2 = Crédito
                // Obtener la descripción del tipo de término (ej: '01 Días')
                termOption = term_types[term_type] || term_type;
            }
        }

        // 🛑 Función ASYNC principal 🛑
        const doPrint = async () => {
            await fetchLocations();
            const deptoDesc = getLocationDescription(client_department_code, true);
            const muniDesc = getLocationDescription(client_municipality_code, true);
            // -------------------------------------------------------------
            // 🛑 PASO 1: CARGA ASÍNCRONA DE RECURSOS (Logo y QR) 🛑
            // -------------------------------------------------------------

            const logoBase64 = await getImageBase64(logoUrl);
            if (dte_url_code) {
                try {
                    // LLAMAMOS Y ESPERAMOS LA PROMESA DE GENERACIÓN
                    await generateQr(dte_url_code);
                } catch (e) {
                    console.error("Fallo al generar QR para PDF:", e);
                }
            }
            const qrBase64Ready = qrDataUrl.value;
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'letter'
            });

            const pageWidth = doc.internal.pageSize.getWidth();
            const marginX = 10;
            let currentY = 10; // Posición Y inicial
            const logoHeight = 15;
            const dteBlockWidth = 120; // Ancho para el bloque QR + datos fiscales (aprox. 1/2 página)
            const dteBlockX = pageWidth - marginX - dteBlockWidth; // Posición X derecha
            const dteBlockRadius = 4; // Radio de redondeo (en mm)
            // -------------------------------------------------------------
            // === 0. LOGO Y DOCUMENTO (ZONA SUPERIOR) ===
            // -------------------------------------------------------------

            // -- 0.1 Logo (Izquierda Superior) --
            if (logoBase64) {
                const logoWidth = 35;
                doc.addImage(logoBase64, 'PNG', marginX, currentY, logoWidth, logoHeight);
            }

            // Posición Y después del Logo para el texto del Emisor
            let yAfterLogo = currentY + logoHeight + 5;

            // -------------------------------------------------------------
            // === 1. EMISOR (DEBAJO DEL LOGO) ===
            // -------------------------------------------------------------

            const issuerX = marginX;
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text(config.emitter_name, issuerX, yAfterLogo);

            doc.setFontSize(8);
            doc.setFont(undefined, 'normal');
            doc.text(`NIT: ${config.emitter_nit}`, issuerX, yAfterLogo + 5);
            doc.text(`RÉGIMEN: ${config.tax_regime}`, issuerX, yAfterLogo + 9);
            doc.text(`DIRECCIÓN: ${config.address}, ${config.city}, ${config.country}`, issuerX, yAfterLogo + 13);
            doc.text(`Tel: ${config.phone_number || 'N/A'} | Email: ${config.email || 'N/A'}`, issuerX, yAfterLogo + 17);

            let yAfterEmisor = yAfterLogo + 25; // Altura final del bloque Emisor

            // -------------------------------------------------------------
            // === 2. BLOQUE FISCAL DTE (QR + CÓDIGOS) - DERECHA SUPERIOR ===
            // -------------------------------------------------------------

            const dteBlockY = currentY; // Inicia arriba, al lado del logo/emisor
            const qrSize = 25;
            const lineHeight = 5;
            const labelWidth = 25;
            const dataX = dteBlockX + qrSize + 5;

            // 1. TÍTULO DEL DOCUMENTO FISCAL
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            //console.table(config);
            console.log(dte_type);
            if(config.send_dte==1){
                doc.text('DOCUMENTO TRIBUTARIO ELECTRÓNICO', dteBlockX + dteBlockWidth / 2, dteBlockY + 4, { align: 'center' });
                doc.setFontSize(10);
                const docTypeText = () => {
                    // Aseguramos que haya un valor dte_type antes de buscar
                    return (dte_type === '01')
                        ? 'FACTURA DE CONSUMIDOR FINAL'
                        : (dte_type === '03')
                            ? 'COMPROBANTE DE CRÉDITO FISCAL'
                            : (dte_type === '05')
                                ? 'NOTA DE CRÉDITO'
                                : (dte_type === '06')
                                    ? 'NOTA DE DÉDITO'
                                    : (dte_type === '14')
                                        ? 'FACTURA DE SUJETO EXCLUIDO' // 🛑 CORRECCIÓN: DTE 14
                                        : (invoice_type || factura.invoice_type || 'COMPROBANTE'); // Fallback
                };
                doc.text(docTypeText() , dteBlockX + dteBlockWidth / 2, dteBlockY + 8, { align: 'center' });

                // 2. Dibujar el recuadro que contiene el QR y los datos
                doc.setDrawColor(0);
                doc.setLineWidth(0.2);
                const dteBlockHeight = 45; // Altura fija
                doc.roundedRect(dteBlockX, dteBlockY, dteBlockWidth, dteBlockHeight, dteBlockRadius, dteBlockRadius, 'S');

                // -- 2.1 QR Code (Izquierda del bloque DTE) --
                if (qrBase64Ready) {
                    doc.addImage(qrBase64Ready, 'PNG', dteBlockX + 1, dteBlockY +10, qrSize, qrSize); // Posicionado debajo del título
                } else {
                    doc.setFontSize(8);
                    doc.text("QR NO DISPONIBLE", dteBlockX + 5, dteBlockY + 25);
                }

                // -- 2.2 Datos Fiscales DTE (A la derecha del QR) --
                let dataY = dteBlockY + 14;
                doc.setFontSize(6);

                // Código de generación
                doc.setFont(undefined, 'bold');
                doc.text('Código de generación:', dataX, dataY);
                doc.setFont(undefined, 'normal');
                doc.text(dte_trace_code || 'N/A', dataX + labelWidth, dataY);
                dataY += lineHeight;

                // Número de control del DTE
                doc.setFont(undefined, 'bold');
                doc.text('No. de control del DTE:', dataX, dataY);
                doc.setFont(undefined, 'normal');
                doc.text(full_number || 'N/A', dataX + labelWidth, dataY);
                dataY += lineHeight;

                // Sello de recepción
                doc.setFont(undefined, 'bold');
                doc.text('Sello de recepción:', dataX, dataY);
                doc.setFont(undefined, 'normal');
                doc.text(dte_stamp_code || 'N/A', dataX + labelWidth, dataY);
                dataY += lineHeight;

                doc.setFont(undefined, 'bold');
                // Fecha de generación (Se añade al final del bloque DTE)
                doc.text(`Fecha y hora generación: ${formatDate(date_issued || factura.fecha)}`, dataX, dataY);

                // 🛑 Actualizamos Y para el resto del documento 🛑
                let yAfterDTEBlock = dteBlockY + dteBlockHeight;
                currentY = Math.max(yAfterEmisor, yAfterDTEBlock) + 5; // El punto más bajo + margen
            }else{
                doc.text('DOCUMENTO TRIBUTARIO', dteBlockX + dteBlockWidth / 2, dteBlockY + 4, { align: 'center' });
                doc.setFontSize(10);
                const docTypeText = ('FACTURA');
                doc.text(docTypeText , dteBlockX + dteBlockWidth / 2, dteBlockY + 8, { align: 'center' });

                // -- 2.2 Datos Fiscales DTE (A la derecha del QR) --
                let dataY = dteBlockY + 14;
                doc.setFontSize(6);
                const dteBlockHeight = 45;
                // Código de generación
                doc.setFont(undefined, 'normal');
                doc.text('NUMERO:', dataX, dataY);
                doc.text(dte_trace_code || 'N/A', dataX + labelWidth, dataY);
                dataY += lineHeight;

                // Fecha de generación (Se añade al final del bloque DTE)
                doc.text(`Fecha y hora generación: ${formatDate(date_issued || factura.fecha)}`, dataX, dataY);

                // 🛑 Actualizamos Y para el resto del documento 🛑
                let yAfterDTEBlock = dteBlockY + dteBlockHeight;
                currentY = Math.max(yAfterEmisor, yAfterDTEBlock) + 5; // El punto más bajo + margen
            }


            // -------------------------------------------------------------
            // -------------------------------------------------------------
            // === 3. INFORMACIÓN DEL CLIENTE (DEBAJO DE TODO) ===
            // -------------------------------------------------------------
            const maxClientNameWidth = 85;
            const clientBlockHeight = 45; // Aumentamos la altura para 5-6 líneas
            const col1X = marginX + 2;
            const col2X = pageWidth / 2 + 5; // Inicia la segunda columna al centro + margen
            const textLineHeight = 6; // Espaciado vertical ligeramente más grande
            let clientY = currentY;

            // Dibujar el recuadro principal del cliente
            doc.setLineWidth(0.2);
            doc.roundedRect(marginX, clientY, pageWidth - (2 * marginX), clientBlockHeight, dteBlockRadius, dteBlockRadius, 'S');

            // Posición Y de inicio dentro del recuadro
            let currentClientY = clientY + 4;

            // === LÍNEA 1 ===

            // COLUMNA 1: Nombre del Cliente
            doc.setFontSize(8);
            doc.setFont(undefined, 'bold');
            doc.text('CLIENTE:', col1X, currentClientY);

            doc.setFont(undefined, 'normal');
            const clientNameText = client_name || 'CONSUMIDOR FINAL';
            const clientNameLines = doc.splitTextToSize(
                clientNameText,
                maxClientNameWidth // Usar el ancho máximo de la columna
            );

            // 3. IMPRIMIR LAS LÍNEAS DEL NOMBRE DEL CLIENTE
            const clientNameStartX = col1X + labelWidth;
            doc.text(clientNameLines, clientNameStartX, currentClientY);

            // 4. CALCULAR EL AVANCE VERTICAL DE ESTA COLUMNA
            const linesCount = clientNameLines.length;
            let verticalAdvance = linesCount * textLineHeight;
            doc.setFont(undefined, 'normal');
            //doc.text(client_name || 'CONSUMIDOR FINAL', col1X + labelWidth, currentClientY);

            // COLUMNA 2: NIT/RUC
            doc.setFont(undefined, 'bold');
            doc.text('DOCUMENTO:', col2X, currentClientY);
            doc.setFont(undefined, 'normal');
            doc.text(client_nit || 'N/A', col2X + labelWidth, currentClientY);

            currentClientY += verticalAdvance;

            // === LÍNEA 2 ===
            // COLUMNA 1: Tipo de Documento y NRC
            const documentTypeDescription = getDocumentTypeText(client_document_type || '36');
            doc.setFont(undefined, 'bold');
            doc.text('TIPO DOC:', col1X, currentClientY);
            doc.setFont(undefined, 'normal');
            doc.text(documentTypeDescription || '36', col1X + labelWidth, currentClientY);

            // COLUMNA 2: NRC
            doc.setFont(undefined, 'bold');
            doc.text('NRC:', col2X, currentClientY);
            doc.setFont(undefined, 'normal');
            doc.text(client_nrc || 'N/A', col2X + labelWidth, currentClientY);

            currentClientY += textLineHeight;

            // === LÍNEA 3 ===
            // COLUMNA 1: Dirección (Dividida si es larga)
            doc.setFont(undefined, 'bold');
            doc.text('DIRECCIÓN:', col1X, currentClientY);
            doc.setFont(undefined, 'normal');
            const addressLines = doc.splitTextToSize(client_address || 'Sin dirección', (pageWidth / 2) - col1X - labelWidth - 5); // Ancho para texto
            doc.text(addressLines, col1X + labelWidth, currentClientY);

            // COLUMNA 2: Correo
            doc.setFont(undefined, 'bold');
            doc.text('CORREO:', col2X, currentClientY);
            doc.setFont(undefined, 'normal');
            doc.text(client_email || 'N/A', col2X + labelWidth, currentClientY);

            currentClientY += textLineHeight * (addressLines.length > 1 ? addressLines.length : 1);

            // === LÍNEA 4/5 ===
            // COLUMNA 1: Actividad y Códigos de Ubicación
            doc.setFont(undefined, 'bold');
            doc.text('DEPTO/MUN:', col1X, currentClientY);
            doc.setFont(undefined, 'normal');
            doc.text(`${deptoDesc.toUpperCase()|| 'N/A'} / ${muniDesc.toUpperCase() || 'N/A'}`, col1X + labelWidth, currentClientY);

            // COLUMNA 2: Código de Actividad
            doc.setFont(undefined, 'bold');
            doc.text('TELÉFONO:', col2X, currentClientY);
            doc.setFont(undefined, 'normal');
            doc.text(client_phone || '99999', col2X + 28, currentClientY); // Más espacio para la etiqueta

            currentClientY += textLineHeight;

            // === LÍNEA FINAL (Descripción de Actividad) ===
            doc.setFont(undefined, 'bold');
            doc.text('ACTIVIDAD:', col1X, currentClientY);
            doc.setFont(undefined, 'normal');
            doc.text(client_activity_code + ' / '+client_activity_desc || '99999 / Venta al consumidor', col1X + labelWidth, currentClientY);

            // COLUMNA 2: Código de Actividad
            /**doc.setFont(undefined, 'bold');
            doc.text('CÓD. ACTIVIDAD:', col1X, currentClientY);
            doc.setFont(undefined, 'normal');
            doc.text(client_activity_code || '99999', col2X + 28, currentClientY);*/ // Más espacio para la etiqueta

            // 🛑 Actualizar Y después del bloque Cliente 🛑
            currentY = clientY + clientBlockHeight + 5;

            // -------------------------------------------------------------
            // === 4. TABLA DE DETALLE (Estructura) ===
            // -------------------------------------------------------------

            const tableY = currentY;
            // Coordenadas y anchos de columna (Definiciones para la tabla)
            const totalW = 20;
            const puW = 20;
            const cantW = 15;
            const descW = pageWidth - (2 * marginX) - totalW - puW - cantW;
            const col = {
                desc: marginX,
                cant: marginX + descW,
                pu: marginX + descW + cantW,
                total: pageWidth - marginX - totalW
            };
            const rowHeight = 7;

            // Encabezados de tabla
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.text('DESCRIPCIÓN', col.desc + 2, tableY + 5);
            doc.text('CANT.', col.cant + cantW / 2, tableY + 5, { align: 'center' });
            doc.text('PRECIO UNIT.', col.pu + puW / 2, tableY + 5, { align: 'center' });
            doc.text('TOTAL', col.total + totalW - 2, tableY + 5, { align: 'right' });
            doc.setLineWidth(0.4);
            doc.line(col.desc, tableY + rowHeight, pageWidth - marginX, tableY + rowHeight);

            let detailY = tableY + rowHeight + 4;
            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');

            // Cuerpo de la tabla
            lines.forEach(line => {
                const lineTotal = line.sub_total_brut || (line.quantity * line.unit_price);
                doc.text(line.description || line.nombre || 'Producto', col.desc + 2, detailY);
                doc.text( parseFloat(line.quantity).toFixed(0), col.cant + cantW / 2, detailY, { align: 'center' });
                doc.text(formatCurrency(line.unit_price, undefined, currencySymbol), col.pu + puW / 2, detailY, { align: 'center' });
                doc.text(formatCurrency(lineTotal, undefined, currencySymbol), col.total + totalW - 2, detailY, { align: 'right' });
                detailY += rowHeight;
            });

            // Línea final de tabla
            doc.setLineWidth(0.4);
            doc.line(col.desc, detailY, pageWidth - marginX, detailY);
            currentY = detailY + 5; // Y después de la tabla

            // -------------------------------------------------------------
            // === 5. TOTALES Y RESUMEN ===
            // -------------------------------------------------------------

            const totalsX = pageWidth - marginX - 60;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            if(dte_type!='01'){
                doc.text('SUBTOTAL BRUTO:', totalsX, currentY);
                doc.text(formatCurrency(sub_total_brut || 0, undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
                currentY += 5;
            }
            if (total_discount > 0) {
                doc.text('DESCUENTO TOTAL:', totalsX, currentY);
                doc.text(`-${formatCurrency((total_discount*-1), undefined, currencySymbol)}`, pageWidth - marginX - 2, currentY, { align: 'right' });
                currentY += 5;

                doc.text('SUBTOTAL NETO:', totalsX, currentY);
                doc.text(formatCurrency(subtotal, undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
                currentY += 5;
            }

            if(!documentTypesWithoutIVA.includes(dte_type)){
                doc.text('IMPUESTO (IVA):', totalsX, currentY);
                doc.text(formatCurrency(total_taxes, undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
                currentY += 4;
            }

            if(dte_type=='03'){
                doc.text('IVA RETENIDO:', totalsX, currentY);
                doc.text(formatCurrency((retention_amount*-1), undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
                currentY += 4;
            }

            if(dte_type=='14'){
                doc.text('RETENCIÓN:', totalsX, currentY);
                doc.text(formatCurrency((retention_amount*-1), undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
                currentY += 4;
            }

            doc.setLineWidth(0.5);
            doc.line(totalsX-5, currentY, pageWidth - marginX, currentY);
            currentY += 5;

            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text('TOTAL A PAGAR:', totalsX - 5, currentY);
            doc.text(formatCurrency(total, undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
            currentY += 10;

            // -------------------------------------------------------------
            // === 6. NOTAS Y PIE DE PÁGINA ===
            // -------------------------------------------------------------

           const textWidth = pageWidth - (2 * marginX) - 50; // Usar el mismo ancho para ambos bloques
            const notesX = marginX;

            // 1. CANTIDAD EN LETRAS

            doc.setFontSize(8);
            doc.setFont(undefined, 'bold');
            doc.text('CANTIDAD EN LETRAS:', notesX, currentY);
            doc.setFont(undefined, 'normal');

            // 🛑 CORRECCIÓN CRÍTICA: Dividir el texto para calcular la altura 🛑
            const totalEnLetrasLines = doc.splitTextToSize(totalEnLetras, textWidth);
            doc.text(totalEnLetrasLines, notesX, currentY + 4);
            // Actualizar Y: (Número de líneas * 4 (altura de línea) + 5 (espacio extra))
            currentY += (totalEnLetrasLines.length * 4) + 4;
            if(dte_type!='05'){
                // --------------------------------------------------------------------------
                // 1. CONDICIÓN DE PAGO (CONTADO vs CRÉDITO)
                // --------------------------------------------------------------------------

                // 1.1 Etiqueta principal de la Condición
                doc.setFontSize(8);
                doc.setFont(undefined, 'bold');
                doc.text('CONDICIÓN:', notesX, currentY);
                doc.setFont(undefined, 'normal');

                // 🛑 NOTA: Se asume que has definido payment_condition_text y term_type_text
                // para mostrar 'Contado' o 'Crédito' y 'Días' o 'Meses' en lugar de 1, 2, 01, 02.
                // Aquí usamos los textos formateados.
                const conditionText = payment_condition === 1 ? 'CONTADO' : 'CRÉDITO';
                doc.text(conditionText, notesX + 30, currentY); // Desplazamos 30mm a la derecha para el dato
                currentY += 5; // Avanzamos a la siguiente línea

                if (payment_condition == 1) {
                    // -------------------------------------------------
                    // 1.2 MODO CONTADO: Mostrar FORMA DE PAGO
                    // -------------------------------------------------

                    doc.setFont(undefined, 'bold');
                    doc.text('FORMA DE PAGO:', notesX, currentY);
                    doc.setFont(undefined, 'normal');

                    // Muestra el tipo de pago (ej: 01 - Billetes y monedas)
                    doc.text(paymentOption, notesX + 30, currentY);
                    currentY += 5; // Avance estándar

                } else {
                    // -------------------------------------------------
                    // 1.3 MODO CRÉDITO: Mostrar Plazo y Vencimiento
                    // -------------------------------------------------

                    // Etiqueta: PLAZO
                    doc.setFont(undefined, 'bold');
                    doc.text('PLAZO:', notesX, currentY);
                    doc.setFont(undefined, 'normal');

                    // Datos: TERM y TERM_TYPE (ej: 30 DÍAS)
                    const plazoText = `${term || 0} ${termOption.toString().toUpperCase()}`; // Asegúrate de traducir term_type a DÍAS/MESES/AÑOS
                    doc.text(plazoText, notesX + 30, currentY);
                    currentY += 5;

                    // Etiqueta: FECHA VENCIMIENTO
                    doc.setFont(undefined, 'bold');
                    doc.text('FECHA VENCIMIENTO: ', notesX, currentY);
                    doc.setFont(undefined, 'normal');

                    // Dato: DUE_DATE
                    doc.text( ' '+due_date, notesX + 30, currentY); // Asume que due_date ya está formateada (YYYY-MM-DD)
                    currentY += 5;
                }
            }


            // --- 2. NOTAS AL PIE (Ahora posicionadas correctamente) ---

            doc.setFont(undefined, 'bold');
            doc.text('NOTAS AL PIE:', notesX, currentY);
            doc.setFont(undefined, 'normal');

            // Usamos el mismo ancho para dividir el texto
            const notesLines = doc.splitTextToSize(footerNotes, textWidth);

            doc.text(notesLines, notesX, currentY + 4);
            currentY += (notesLines.length * 4) + 5;
            // --- 3. BLOQUE DE ENTREGA Y RECEPCIÓN (Firmas y Datos) ---

            doc.setFontSize(8);
            const blockStartSpace = 8;
            const blockHeight = 25; // Altura fija del recuadro
            //const lineHeight = 4; // Altura de línea para texto 8pt

            // 1. DIBUJAR EL RECUADRO Y DEFINIR POSICIONES
            currentY += blockStartSpace;
            const signatureBlockY = currentY;
            const blockWidth = pageWidth - (2 * marginX);

            doc.setLineWidth(0.2);
            doc.roundedRect(marginX, signatureBlockY, blockWidth, blockHeight, 3, 3, 'S');

            // Definición de coordenadas y ancho del texto
            const col1StartX = marginX + 3;
            const col2StartX = pageWidth / 2 + 5;
            const textDataWidth = (blockWidth / 2) - 10; // Ancho máximo para los nombres (~85mm)

            // Variables de posición Y iniciales
            let yLabelName = signatureBlockY + 4; // Y para la etiqueta del Nombre (Col 1 y Col 2)
            let yLabelDoc = signatureBlockY + 16; // Y para la etiqueta del Documento (Col 1 y Col 2)

            // Datos dinámicos (variables de ámbito superior)
            const nombreEntrega = config.emitter_name || '________________________________';
            const documentoEntrega = config.emitter_nit || '________________________________';
            const nombreRecibe = client_name || '________________________________';
            const documentoRecibe = client_nit || '________________________________';


            // --------------------------------------------------
            // 2. COLUMNA 1: ENTREGA (Izquierda)
            // --------------------------------------------------
            // **Nombre entrega**
            doc.setFont(undefined, 'bold');
            doc.text('Nombre entrega:', col1StartX, yLabelName);
            doc.setFont(undefined, 'normal');

            // Ajuste de texto y Altura
            const entregaNameLines = doc.splitTextToSize(nombreEntrega, textDataWidth);
            doc.text(entregaNameLines, col1StartX, yLabelName + lineHeight);

            // Línea de firma (Nombre)
            //let yLineName = yLabelName + (entregaNameLines.length * lineHeight) + 1;
            //doc.line(col1StartX, yLineName, col1StartX + textDataWidth, yLineName);


            // **Documento entrega**
            doc.setFont(undefined, 'bold');
            doc.text('Documento entrega:', col1StartX, yLabelDoc);
            doc.setFont(undefined, 'normal');

            // Datos y línea (Alineados con yLabelDoc)
            doc.text(documentoEntrega, col1StartX, yLabelDoc + lineHeight);
            //doc.line(col1StartX, yLabelDoc + lineHeight + 1, col1StartX + textDataWidth, yLabelDoc + lineHeight + 1);


            // --------------------------------------------------
            // 3. COLUMNA 2: RECIBE (Derecha) - Usando las mismas coordenadas Y
            // --------------------------------------------------

            // **Nombre recibe**
            doc.setFont(undefined, 'bold');
            doc.text('Nombre recibe:', col2StartX, yLabelName); // Usamos yLabelName de la Col 1
            doc.setFont(undefined, 'normal');

            // Ajuste de texto y Altura
            const recibeNameLines = doc.splitTextToSize(nombreRecibe, textDataWidth);
            doc.text(recibeNameLines, col2StartX, yLabelName + lineHeight);

            // Línea de firma (Nombre) - Usamos la misma Y calculada para Col 1
            //doc.line(col2StartX, yLineName, col2StartX + textDataWidth, yLineName);


            // **Documento recibe**
            doc.setFont(undefined, 'bold');
            doc.text('Documento recibe:', col2StartX, yLabelDoc); // Usamos yLabelDoc de la Col 1
            doc.setFont(undefined, 'normal');

            // Datos y línea (Alineados con yLabelDoc)
            doc.text(documentoRecibe, col2StartX, yLabelDoc + lineHeight);
            //doc.line(col2StartX, yLabelDoc + lineHeight + 1, col2StartX + textDataWidth, col2X + textDataWidth);


            // --------------------------------------------------
            // 4. Actualización Final
            // --------------------------------------------------
            doc.setFont(undefined, 'normal');
            currentY = signatureBlockY + blockHeight + 5; // Avanzar Y al final del recuadro + margen
            // Marca de Reimpresión
            if (isReimpresion) {
                const footerY = doc.internal.pageSize.getHeight() - 15;
                doc.setFontSize(12);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(200, 0, 0);
                doc.text('**REIMPRESION**', pageWidth / 2, footerY, { align: 'center' });
                doc.setTextColor(0);
            }

            // -------------------------------------------------------------
            // === 7. FINALIZACIÓN Y APERTURA ===
            // -------------------------------------------------------------

            const fullNumber = full_number || factura.full_number;
            const fileName =`${fullNumber}.pdf`;

            const blob = doc.output('blob');
            const blobUrl = URL.createObjectURL(blob);
            const clientEmail = factura.header.client_email || factura.receiver?.email;
            const facturaNumber = factura.header.full_number;
            const a = document.createElement('a');
            a.href = blobUrl;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            a.download = fileName;

            a.click();
            if (clientEmail && facturaNumber) {
                // Envolvemos la conversión en una Promise para manejarla con await o .then()
                const base64Promise = new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = function() {
                        // reader.result es el DataURL (ej: "data:application/pdf;base64,...")
                        // Tomamos solo la parte después de "base64,"
                        const base64data = reader.result.split('base64,')[1];
                        resolve(base64data);
                    }
                });

                const pdfBase64 = await base64Promise; // Esperamos la conversión

                // Ejecutamos la función de envío de correo
                await emailDTE(pdfBase64, clientEmail, facturaNumber, dte_trace_code);
            }


            URL.revokeObjectURL(blobUrl);

            resolve();
        }

        doPrint();
    });
}

const emailDTE = async (pdfBase64, email, facturaNumber,dte_trace_code) => {
    if (!email) {
        console.warn("Email del cliente no disponible para el envío.");
        return;
    }

    try {
        const apiURL='https://dicovaspos.therealdevs.pro/index.php/';
        const token = localStorage.getItem('accessToken');
        const response = await axios.post(`${apiURL}email/send_dte`, {
            email: email,
            subject: `Copia de DTE ${facturaNumber} [Enviado por DICOVAS]`,
            filename: `DTE_${facturaNumber}.pdf`,
            pdf_base64: pdfBase64,
            trace_code:dte_trace_code
        },
        { headers: { 'Authorization': `Bearer ${token}` } }, {keepalive: true});

        console.log(`DTE enviado a ${email}. Mensaje:`, response.data.message);
        //console.log(`DTE enviado a ${email}. Mensaje:`, response);
        // Opcional: mostrar una notificación de éxito al usuario

    } catch (error) {
        console.error("Fallo al enviar el DTE por correo:", error.response?.data?.error || error.message);
        // Opcional: mostrar una notificación de error
    }
};